import { PageHeader , Button , Card, CardContent , Input , Skeleton } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Save } from "@/components/icons";
import {
  TranslationImportFeedbackMessage,
  TranslationKeysForm,
  type TranslationImportFeedback,
} from "@/components/translation-keys-form";
import { getTranslations, saveTranslations } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { formatLocaleLabelWithTag } from "@/lib/locales";
import { parseTranslationJsonImport } from "@/lib/translation-json-import";
import {
  buildTranslationDetailPath,
  REQUIRED_TRANSLATION_KEYS,
  TRANSLATION_KEYS,
} from "@/lib/translations";
import type { GetTranslationsResp } from "@/lib/types";

export function EditTranslationPage() {
  const { t } = useTranslation();
  const { variant = "" } = useParams<{ variant: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [importFeedback, setImportFeedback] = useState<TranslationImportFeedback | null>(null);

  const query = useQuery<GetTranslationsResp>({
    queryKey: ["translations", variant],
    queryFn: () => getTranslations({ variant }),
    enabled: variant.length > 0,
  });

  useEffect(() => {
    if (query.data) {
      setKeys(query.data.keys);
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () => saveTranslations({ variant, keys }),
    onSuccess: async () => {
      toast.success(t("translations.savedToast", { variant }));
      await queryClient.invalidateQueries({ queryKey: ["translations", variant] });
      navigate(buildTranslationDetailPath(variant));
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const allFilled = TRANSLATION_KEYS.every((key) => keys[key]?.trim());

  async function handleImportFile(file: File) {
    const rawText = await file.text();
    const result = parseTranslationJsonImport(rawText, REQUIRED_TRANSLATION_KEYS);
    if (!result.ok) {
      setImportFeedback(null);
      toast.error(result.message);
      return;
    }

    setKeys(result.keys);
    setImportFeedback({
      fileName: file.name,
      importedCount: result.importedCount,
      unknownKeys: result.unknownKeys,
      missingRequiredKeys: result.missingRequiredKeys,
      invalidValueKeys: result.invalidValueKeys,
    });
    toast.success(
      t("translationImport.imported", { count: result.importedCount, fileName: file.name }),
    );
  }

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">{t("translations.loadOneFailed")}</p>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("translations.title")}
        backTo={buildTranslationDetailPath(query.data.variant)}
        backLabel={t("translations.backToTranslation")}
        title={t("translations.editTitle")}
        subtitle={formatLocaleLabelWithTag(query.data.variant)}
        actions={
          <>
            <Input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) {
                  return;
                }
                void handleImportFile(file);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => importInputRef.current?.click()}
            >
              {t("translations.uploadJson")}
            </Button>
            <Button
              disabled={!allFilled || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? (
                t("common.saving")
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {t("common.save")}
                </>
              )}
            </Button>
          </>
        }
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <p className="text-xs text-muted-foreground">
            {t("translations.acceptsNote")}
          </p>
          {importFeedback && <TranslationImportFeedbackMessage feedback={importFeedback} />}
          <TranslationKeysForm keys={keys} onChange={setKeys} />
        </CardContent>
      </Card>
    </div>
  );
}